import { ChangeDetectionStrategy, Component, Inject, OnInit, signal, ViewEncapsulation } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, of, switchMap } from 'rxjs';
import { ActivatedRoute } from '@angular/router';

import { PageSlotModule } from '@spartacus/storefront';

import { RecipeSearchComponent } from '@seb/web-common/core/components/recipe/recipe-search/recipe-search.component';
import { AbstractPageComponent } from '@seb/web-common/core/templates/abstract-page/abstract-page.component';
import { CoreModalService, IRichParagraph, PositionEnum, ThemesEnum } from '@seb/web-design-system/core';
import { argumentsSkeleton, WC_CONFIG_PROVIDER, WcConfig } from '@seb/web-common/core/models';
import { SdArgumentsComponent, IArguments } from '@seb/web-design-system/arguments';
import { CustomJsonLdDirective } from '@seb/web-common/core/directives';
import { RecipeService } from '@seb/web-common/core/services';

@Component({
  selector: 'wc-recipe-list-page',
  templateUrl: './recipe-list-page.component.html',
  styleUrls: ['./recipe-list-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    SdArgumentsComponent,
    RecipeSearchComponent,
    PageSlotModule,
    CustomJsonLdDirective
  ],
  providers: [CoreModalService]
})
export class RecipeListPageComponent extends AbstractPageComponent implements OnInit {
  readonly layoutTextConfig = PositionEnum.center;
  readonly textConfig = PositionEnum.center;
  themesConfig = ThemesEnum.default;

  arguments = signal<IArguments>({ ...argumentsSkeleton, secondaryMedia: undefined });
  isArgumentsLoading = signal(true);

  constructor(
    private route: ActivatedRoute,
    private recipeService: RecipeService,
    private translateService: TranslateService,
    @Inject(WC_CONFIG_PROVIDER) wcConfig: WcConfig
  ) {
    super();

    this.snippetsOptions = { images: true };
    this.themesConfig = wcConfig.isThemeDark ? ThemesEnum.dark : this.themesConfig;
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.subscriptions.add(
      this.route.queryParams.pipe(
        switchMap((params) => {
          const searchKey = params['query']?.split(':')[0];
          return combineLatest([of(searchKey), this.recipeService.search(searchKey || '', 0, 0)]);
        })
      ).subscribe(([searchKey, rsp]) => {
        const content: IRichParagraph = !!rsp && rsp.page?.totalElements > 0 ? {
          heading: {
            data: {
              title: searchKey || this.translateService.instant('recipes.list.allResult'),
              subtitle: this.translateService.instant('recipes.list.result', { result: rsp?.page?.totalElements }),
              description: this.translateService.instant('recipes.list.description')
            }
          }
        } : {
          heading: {
            data: {
              description: this.translateService.instant('recipes.list.noResult', { searchKey })
            }
          }
        };

        this.arguments.set({
          media: { url: '/assets/images/recipe_list_arguments.jpg' },
          content
        });

        this.isArgumentsLoading.set(false);
      })
    );
  }
}
